import { motion } from 'framer-motion';
import { ArrowRight, Lock, Mail } from 'lucide-react';
import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { isApiError, userMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [shake, setShake] = React.useState(0);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user = await login(username.trim(), password);
      toast.success(`Welcome back, ${user.full_name?.split(' ')[0] || user.username}!`);
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        isApiError(err, 401)
          ? 'Wrong email/username or password. Check them and try again.'
          : isApiError(err, 403)
            ? 'This account is disabled. Please contact support.'
            : isApiError(err, 422)
              ? 'Enter your email or username and your password.'
              : userMessage(err, "Couldn't sign you in. Please try again."),
      );
      setShake((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <h1 className="text-3xl font-extrabold tracking-tight">Welcome back</h1>
      <p className="mt-2 text-muted-foreground">Sign in to check a visa or see your results.</p>

      <motion.form
        key={shake}
        animate={shake ? { x: [0, -10, 10, -6, 6, 0] } : undefined}
        transition={{ duration: 0.4 }}
        onSubmit={onSubmit}
        className="mt-8 space-y-5"
      >
        <Field label="Email or username" htmlFor="username">
          <Input
            id="username"
            autoComplete="username"
            icon={<Mail />}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoFocus
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            icon={<Lock />}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
          >
            {error}
          </motion.p>
        )}
        <Button type="submit" variant="brand" size="lg" className="group w-full" loading={busy}>
          Sign in <ArrowRight />
        </Button>
      </motion.form>
      <p className="mt-4 text-center text-sm"><Link to="/password-reset" className="font-semibold text-primary-ink hover:underline">Forgot your password?</Link></p>

      <p className="mt-8 text-center text-sm text-muted-foreground">
        New here?{' '}
        <Link to="/register" className="font-semibold text-primary-ink hover:underline">
          Create an account
        </Link>{' '}
        and get free coins.
      </p>
    </AuthLayout>
  );
}
